// app-auth.guard.ts
import { inject } from '@angular/core';
import { CanActivateChildFn, Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from './auth.service';
import { NavigationService } from './navigation.service';

export const appAuthGuard: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  
  const auth = inject(AuthService);
  const navigation = inject(NavigationService);
  const router = inject(Router);

  // Check if user is authenticated
  if (!auth.isAuthenticated() || auth.user.getValue() === null) {
    // Store attempted URL for redirect after login
    if (!state.url.includes('login')) {
      auth.triedUrl = state.url;
    }
    
    return router.parseUrl('/login');
  }

  const currentUser = auth.getCurrentUser();
  
  if (!currentUser) {
    auth.triedUrl = state.url;
    return router.parseUrl('/login');
  }

  const requestedRoute = state.url;
  const userRole = currentUser.role;
  const userRoleLower = userRole.toLowerCase();

  // ALL roles require approval (including SuperAdmin and Admin)
  if (!currentUser.isApproved) {
    return router.parseUrl('/unauthorized');
  }

  // SuperAdmin and Admin have full access (after approval check)
  if (userRoleLower === 'superadmin' || userRoleLower === 'admin') {
    return true;
  }

  // Check if the route is allowed for the user's role (pass original case)
  const isAllowed = navigation.isRouteAllowedForRole(requestedRoute, userRole);

  if (!isAllowed) {
    // Redirect to user's default route instead of blocking
    const defaultRoute = navigation.getDefaultRouteForRole(userRole);
    return router.parseUrl(defaultRoute);
  }

  return true;
};


// OPTIONAL: Create a separate guard for hospital selection if needed
export const hospitalSelectionGuard: CanActivateChildFn = (childRoute, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const user = auth.user.getValue();
  
  // If user is not logged in, redirect to login
  if (!user) {
    return router.parseUrl('/login');
  }

  // If user doesn't have a hospital ID, redirect to hospital selection
  const hospitalId = localStorage.getItem('fhir_hospital_id');
  if (!hospitalId && !user.isSuperAdmin) {
    return router.parseUrl('/hospital-selection');
  }

  return true;
};